import React, { useEffect, useState, useCallback } from "react";
import { FlatList, Platform, StyleSheet, TouchableOpacity, View, Modal, SafeAreaView, useWindowDimensions } from "react-native";
import { useFocusEffect } from "@react-navigation/native";
import ListingCard from "../components/ListingCard";
import config from "../config/config";
import Screen from "../components/Screen";
import AppText from "../components/AppText";
import IconTouchable from "../components/IconTouchable";
import useUser from "../hooks/useUser";
import BlockSeperator from "../components/BlockSeperator";
import useProducts from "../hooks/useProducts";
import ListItem from "../components/lists/ListItem";
import Icon from "../components/Icon";
import AppButton from "../components/AppButton";

function DashboardScreen({ navigation }) {
    const { user } = useUser();
    const { products, getProducts, loading } = useProducts();
    const [extraData, setExtraData] = useState(false);
    const [categories, setCategories] = useState();

    const window = useWindowDimensions();

    const [modalVisible, setModalVisible] = useState(false);

    const [selectedStatus, setSelectedStatus] = useState("for sale");
    const [selectedCategory, setSelectedCategory] = useState(false);

    useEffect(() => {
        getProducts();
    }, []);

    useEffect(() => {
        if (!products) {
            return;
        }

        setCategories([...new Set(products.map((product) => product.category))]);
    }, [products]);

    // Used to refresh the flatlist after new product data has been received.
    useFocusEffect(
        useCallback(() => {
            setExtraData(!extraData);
        }, [])
    );

    return (
        <Screen
            title={selectedCategory}
            capitulize={true}
            subTitle={selectedStatus}
            navigation={navigation}
            loading={loading}
            rightIconComponent={<IconTouchable name="tools" onPress={() => navigation.navigate("Tools")} />}
        >
            <AppButton title="Dashboard New" color={config.colors.tertiary} onPress={() => navigation.navigate("DashboardReskin")} />

            <View style={styles.selectorIcons}>
                <TouchableOpacity style={styles.selector} onPress={() => setSelectedStatus("for sale")}>
                    <AppText>For Sale - </AppText>
                    <AppText>{products.filter((x) => x.status == "for sale").length}</AppText>
                </TouchableOpacity>
                <TouchableOpacity style={styles.selector} onPress={() => setSelectedStatus("sold")}>
                    <AppText>Sold - </AppText>
                    <AppText>{products.filter((x) => x.status == "sold").length}</AppText>
                </TouchableOpacity>
                <TouchableOpacity style={styles.selector} onPress={() => setSelectedStatus("archive")}>
                    <AppText>Archive - </AppText>
                    <AppText>{products.filter((x) => x.status == "archive").length}</AppText>
                </TouchableOpacity>
            </View>
            <FlatList
                data={
                    selectedCategory
                        ? products.filter((x) => x.category == selectedCategory).filter((y) => y.status == selectedStatus)
                        : products.filter((x) => x.status == selectedStatus)
                }
                key={window.width > 758 ? "h" : "v"}
                numColumns={window.width > 758 ? 3 : 1}
                extraData={extraData}
                keyExtractor={(item) => item._id}
                horizontal={false}
                showsVerticalScrollIndicator={false}
                ListHeaderComponent={
                    <View style={{ marginTop: 10 }}>
                        <IconTouchable
                            name="filter"
                            size={55}
                            onPress={() => {
                                setExtraData(null);
                                setModalVisible(true);
                            }}
                        />
                    </View>
                }
                ListEmptyComponent={
                    <View style={{ alignItems: "center", paddingTop: 50 }}>
                        <AppText style={styles.selectorText} numberOfLines={1}>
                            No Results,
                        </AppText>
                        <AppText>Please amend your search above</AppText>
                    </View>
                }
                renderItem={({ item }) => <ListingCard product={item} navigation={navigation} />}
            />

            <Modal animationType="fade" transparent={false} visible={modalVisible}>
                <SafeAreaView>
                    <ListItem title={"Categories"} />
                    <ListItem
                        title={"All"}
                        utilityComponent={<Icon name="arrow-right" iconColor={config.colors.tertiary} backgroundColor={config.colors.white} />}
                        onPress={() => {
                            setSelectedCategory(false);
                            setModalVisible(false);
                        }}
                    />
                    {categories &&
                        categories.map((category) => (
                            <ListItem
                                key={category}
                                title={category}
                                utilityComponent={<Icon name="arrow-right" iconColor={config.colors.tertiary} backgroundColor={config.colors.white} />}
                                onPress={() => {
                                    setSelectedCategory(category);
                                    setModalVisible(false);
                                }}
                            />
                        ))}
                    <View style={{ padding: 20 }}>
                        <IconTouchable
                            name="close"
                            size={55}
                            onPress={() => {
                                setExtraData(null);
                                setModalVisible(false);
                            }}
                        />
                    </View>
                </SafeAreaView>
            </Modal>
        </Screen>
    );
}

const styles = StyleSheet.create({
    text: {
        alignSelf: "center",
        fontSize: 16,
        fontWeight: "900",
        color: config.colors.white,
        textTransform: "capitalize",
    },
    selectorIcons: {
        flexDirection: "row",
        justifyContent: "space-evenly",
        paddingVertical: 5,
    },
    typeSelectorIcons: {
        flexDirection: "row",
        justifyContent: "space-around",
        paddingBottom: 30,
    },
    buttonBoxLeft: {
        alignItems: "center",
        flexDirection: "row",
    },
    buttonBoxRight: {
        alignItems: "center",
        flexDirection: "row",
    },
    selector: {
        justifyContent: "center",
        alignItems: "center",
        flexDirection: "row",
        backgroundColor: config.colors.secondary,
        borderRadius: 5,
        paddingHorizontal: 10,
    },
});

export default DashboardScreen;
